import {
  QUESTION_KINDS,
  type QuestionKind,
} from './shared-question-model-kind.consts';
import {
  OPTION_RENDER_TYPES,
  QUESTION_SHAPES,
  type OptionRenderType,
  type QuestionShape,
} from './shared-question-model-shape.consts';

export interface QuestionKindInput {
  readonly questionShape: QuestionShape;
  /** `media` is what makes a multiple-choice question an image choice. */
  readonly optionRenderType: OptionRenderType | null;
  /** A rating question with sub-questions is a matrix. */
  readonly subQuestionCount: number;
  /** Contact info is an open-ended question carrying the contact tag. */
  readonly isContactInfo: boolean;
}

/**
 * Resolves a question's kind from typed, framework-neutral inputs only.
 * Shared verbatim by the builder (which authors kinds from its GraphQL
 * `QuestionType`/`OptionRenderType`) and the responder (which answers
 * against them) — each side owns the adapter that maps its own generated
 * enum onto `QuestionShape`/`OptionRenderType` before calling this.
 *
 * The exhaustive `switch` with no `default` is deliberate: adding a
 * `QuestionShape` member without a case here is a compile error, not a
 * silent fall-through.
 */
export function resolveQuestionKind({
  questionShape,
  optionRenderType,
  subQuestionCount,
  isContactInfo,
}: QuestionKindInput): QuestionKind {
  switch (questionShape) {
    case QUESTION_SHAPES.Message:
      return QUESTION_KINDS.Message;
    case QUESTION_SHAPES.MobileApp:
      return QUESTION_KINDS.MobileApp;
    case QUESTION_SHAPES.Task:
      return QUESTION_KINDS.Task;
    case QUESTION_SHAPES.Url:
      return QUESTION_KINDS.Url;
    case QUESTION_SHAPES.Ranking:
      return QUESTION_KINDS.Ranking;
    case QUESTION_SHAPES.Rating:
      return subQuestionCount > 0
        ? QUESTION_KINDS.Matrix
        : QUESTION_KINDS.Rating;
    case QUESTION_SHAPES.MultipleChoice:
      return optionRenderType === OPTION_RENDER_TYPES.Media
        ? QUESTION_KINDS.ImageChoice
        : QUESTION_KINDS.Choice;
    case QUESTION_SHAPES.OpenEnded:
      return isContactInfo
        ? QUESTION_KINDS.ContactInfo
        : QUESTION_KINDS.OpenEnded;
    default: {
      const unexpected: never = questionShape;
      throw new Error('Unexpected question shape: ' + String(unexpected));
    }
  }
}
